import { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import {
  TechnologyLifecycleStage,
  type TechRadarBlip,
  type TechRadarSector,
} from '../../graphql/types';
import './TechRadarChart.css';

export interface TechRadarChartProps {
  sectors: TechRadarSector[];
  blips: TechRadarBlip[];
  /** Ids of blips matching the active filters; non-matching blips are dimmed. */
  matchedIds: Set<string>;
  /** Whether any filter is active (drives the dimming of non-matches). */
  filtersActive: boolean;
  selectedId?: string | null;
  onSelect: (blip: TechRadarBlip) => void;
  /** Which wedge a blip belongs to. Defaults to its sector; override to drill into families. */
  sectorOf?: (blip: TechRadarBlip) => string;
  size?: number;
}

/** Rings from the strategic centre (Adopt) outward to phase-out (Sunset). */
export const RING_ORDER: TechnologyLifecycleStage[] = [
  TechnologyLifecycleStage.Adopt,
  TechnologyLifecycleStage.Hold,
  TechnologyLifecycleStage.Evaluate,
  TechnologyLifecycleStage.Sunset,
];

/**
 * Fixed fraction of `maxRadius` each ring occupies — a deliberately non-uniform split
 * (issue #1864) that more than triples Adopt's on-screen area share (6.25% -> 19.36% of the
 * full circle vs. the old equal-width split), since it's the most-populated ring. This widens
 * Adopt's *radius* share the most of the four rings, but ring area grows with radius, so a
 * wider inner band doesn't make every outer ring's area smaller in exchange — Hold's area share
 * actually grows slightly (18.75% -> 26.88%) even though its band width shrinks, because its
 * band sits farther from center. Fixed, not data-driven: the same chart renders both the full
 * radar and a filtered dashboard-widget subset, and a count-driven split would shift ring
 * boundaries between those two views. Sums to 1.
 *
 * Keyed by `TechnologyLifecycleStage`, not positional — same reason `RING_COLORS` below is a
 * `Record` and not a parallel array: a positional `number[]` gives no compile-time or runtime
 * signal if a future stage is added to the enum without a matching fraction, and this and
 * `RING_ORDER` would then silently collapse the unhandled ring to zero width (review finding).
 */
export const RING_FRACTIONS: Record<TechnologyLifecycleStage, number> = {
  [TechnologyLifecycleStage.Adopt]: 0.44,
  [TechnologyLifecycleStage.Hold]: 0.24,
  [TechnologyLifecycleStage.Evaluate]: 0.18,
  [TechnologyLifecycleStage.Sunset]: 0.14,
};

/** `RING_FRACTIONS` in `RING_ORDER` order — the shape `computeRingOuterRadii` operates on. */
const RING_FRACTIONS_ORDERED: readonly number[] = RING_ORDER.map(
  // stage is a fixed TechnologyLifecycleStage enum value — not user-controlled
  // eslint-disable-next-line security/detect-object-injection
  (stage) => RING_FRACTIONS[stage]
);

/**
 * Minimum on-screen band width (px) for any ring, regardless of `size`. Below this a "new"-blip
 * outline (fixed `r=10`) and the 10px ring label font stop reading legibly.
 */
export const MIN_RING_WIDTH_PX = 20;

interface RingSlot {
  readonly fraction: number;
  width: number;
  clamped: boolean;
}

/**
 * Derives each ring's outer radius (cumulative, outer edge of its band) from `RING_FRACTIONS`
 * (in `RING_ORDER` order). If the fixed-fraction split would put a ring's band under
 * `MIN_RING_WIDTH_PX` (e.g. Evaluate and Sunset at the dashboard widget's `size=300`), that
 * ring is clamped to the minimum and the remaining radius is redistributed proportionally
 * among the still-unclamped rings, keeping their relative ratio — repeated until every band
 * clears the minimum (clamping a ring only grows the others' share, so this converges in at
 * most `RING_FRACTIONS_ORDERED.length` passes).
 *
 * If `maxRadius` is too small for every ring to clear `MIN_RING_WIDTH_PX` at once (below
 * `RING_FRACTIONS_ORDERED.length * MIN_RING_WIDTH_PX` — not reached by either of the app's
 * current sizes, 300 or 600, but this is an exported helper with no caller-size guarantee),
 * the minimum can't be honored without pushing outer radii past `maxRadius`, so this falls
 * back to the raw fractional split instead — bands may then read below the legibility floor,
 * but the returned radii always stay within `maxRadius`.
 */
export function computeRingOuterRadii(maxRadius: number): number[] {
  if (maxRadius < RING_FRACTIONS_ORDERED.length * MIN_RING_WIDTH_PX) {
    let unclampedAcc = 0;
    return RING_FRACTIONS_ORDERED.map((fraction) => (unclampedAcc += fraction * maxRadius));
  }

  const slots: RingSlot[] = RING_FRACTIONS_ORDERED.map((fraction) => ({
    fraction,
    width: 0,
    clamped: false,
  }));
  let remainingRadius = maxRadius;
  let remainingFractionSum = 1;

  let changed = true;
  while (changed) {
    changed = false;
    for (const slot of slots) {
      if (slot.clamped) continue;
      slot.width =
        remainingFractionSum > 0 ? (slot.fraction / remainingFractionSum) * remainingRadius : 0;
    }
    for (const slot of slots) {
      if (slot.clamped) continue;
      if (slot.width < MIN_RING_WIDTH_PX) {
        slot.clamped = true;
        slot.width = MIN_RING_WIDTH_PX;
        remainingRadius -= MIN_RING_WIDTH_PX;
        remainingFractionSum -= slot.fraction;
        changed = true;
      }
    }
  }

  let acc = 0;
  return slots.map((slot) => (acc += slot.width));
}

/**
 * Ring colors — the styling spec's accessibility-first semantic palette
 * (styling-spec.md §Semantic Colors, WCAG 2.1 AA). Ring identity is also encoded by
 * concentric position + labels + blip tooltips, so it never relies on color alone.
 */
export const RING_COLORS: Record<TechnologyLifecycleStage, string> = {
  [TechnologyLifecycleStage.Adopt]: '#2d7a3e', // success green
  [TechnologyLifecycleStage.Hold]: '#e65100', // warning orange
  [TechnologyLifecycleStage.Evaluate]: '#0277bd', // info blue
  [TechnologyLifecycleStage.Sunset]: '#c62828', // error red
};

/**
 * Per-sector wedge palette — the styling spec's approved data-visualization series
 * (styling-spec.md §Data Visualization, Light Mode). Distinct from the semantic ring colors.
 * Sectors beyond six cycle; wedge identity is also encoded by labels + the matching legend swatch.
 */
export const SECTOR_PALETTE = [
  '#40528f', // Tyler Blue
  '#8f9b49', // Tyler Green
  '#e27022', // Orange
  '#6c4877', // Purple
  '#0e91a2', // Teal
  '#b97510', // Dark Orange
];

/** Stable wedge color for the sector at a given display index. */
export function sectorColor(index: number): string {
  return SECTOR_PALETTE[index % SECTOR_PALETTE.length];
}

/**
 * Deterministic pseudo-random value in [0, 1) from a string key + salt (FNV-1a).
 * Used to scatter blips within a cell consistently across re-renders.
 */
function hashUnit(key: string, salt: number): number {
  let h = (2166136261 ^ salt) >>> 0;
  for (let i = 0; i < key.length; i += 1) {
    h = Math.imul(h ^ key.charCodeAt(i), 16777619);
  }
  return ((h >>> 0) % 100000) / 100000;
}

interface PlacedBlip {
  blip: TechRadarBlip;
  x: number;
  y: number;
}

export function TechRadarChart({
  sectors,
  blips,
  matchedIds,
  filtersActive,
  selectedId,
  onSelect,
  sectorOf = (b) => b.sector,
  size = 600,
}: TechRadarChartProps): JSX.Element {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    if (sectors.length === 0) return;

    // Space reserved around the plot for the ring/sector labels. Scales with `size` so compact
    // renders (e.g. the dashboard widget) keep a usable plot instead of shrinking it to a dot.
    // At the 600px full-page default this evaluates to 96, matching the previous fixed value.
    const margin = Math.round(size * 0.16);
    const maxRadius = size / 2 - margin;
    const cx = size / 2;
    const cy = size / 2;

    const sectorCount = sectors.length;
    const angleSlice = (Math.PI * 2) / sectorCount;
    const ringCount = RING_ORDER.length;
    // Cumulative outer radius per ring (fixed-fraction split, #1864), not an equal-width step.
    const ringOuterRadii = computeRingOuterRadii(maxRadius);
    const ringOuterRadius = (i: number): number => ringOuterRadii.at(i) ?? maxRadius;
    const ringInnerRadius = (i: number): number => (i === 0 ? 0 : ringOuterRadius(i - 1));

    const sectorIndex = new Map(sectors.map((s, i) => [s.label, i]));
    const ringIndex = new Map(RING_ORDER.map((r, i) => [r, i]));

    const g = svg.append('g').attr('transform', `translate(${cx},${cy})`);
    const defs = svg.append('defs');

    // Per-sector radial transparency gradient, centered on the radar (userSpaceOnUse so the fade is
    // concentric with the rings): bold at the centre (the desirable Adopt core), fading outward.
    sectors.forEach((_sector, i) => {
      const color = sectorColor(i);
      const grad = defs
        .append('radialGradient')
        .attr('id', `tr-sector-grad-${i}`)
        .attr('gradientUnits', 'userSpaceOnUse')
        .attr('cx', 0)
        .attr('cy', 0)
        .attr('r', maxRadius);
      grad.append('stop').attr('offset', '0%').attr('stop-color', color).attr('stop-opacity', 0.32);
      grad
        .append('stop')
        .attr('offset', '100%')
        .attr('stop-color', color)
        .attr('stop-opacity', 0.04);
    });

    // Translucent per-sector wedge fills (drawn first so the ring grid + labels sit on top).
    // d3.arc measures angles clockwise from the top, matching the boundary lines/labels below.
    const wedge = d3
      .arc<number>()
      .innerRadius(0)
      .outerRadius(maxRadius)
      .startAngle((i) => angleSlice * i)
      .endAngle((i) => angleSlice * (i + 1));
    sectors.forEach((_sector, i) => {
      g.append('path')
        .attr('class', 'tech-radar-wedge')
        .attr('d', wedge(i) ?? '')
        .attr('fill', `url(#tr-sector-grad-${i})`);
    });

    // Concentric ring bands: subtle alternating greyscale fill makes the rings distinct beyond the
    // boundary circles, drawn under the sector wedges' edges via low opacity.
    for (let i = 0; i < ringCount; i += 1) {
      if (i % 2 === 1) {
        g.append('path')
          .attr(
            'd',
            d3
              .arc<null>()
              .innerRadius(ringInnerRadius(i))
              .outerRadius(ringOuterRadius(i))
              .startAngle(0)
              .endAngle(Math.PI * 2)(null) ?? ''
          )
          .attr('fill', 'var(--forge-theme-text-primary, #212121)')
          .attr('fill-opacity', 0.04);
      }
    }

    // Concentric ring boundary circles (outer edge of each band), labelled along the top axis.
    for (let i = 0; i < ringCount; i += 1) {
      const outer = ringOuterRadius(i);
      const inner = ringInnerRadius(i);
      g.append('circle')
        .attr('class', 'tech-radar-ring-boundary')
        .attr('r', outer)
        .attr('fill', 'none')
        .attr('stroke', 'var(--forge-theme-text-medium, #9e9e9e)')
        .attr('stroke-width', i === ringCount - 1 ? 1.5 : 1);

      g.append('text')
        .attr('x', 0)
        .attr('y', -((inner + outer) / 2))
        .attr('dy', '0.35em')
        .attr('text-anchor', 'middle')
        .attr('font-size', '10px')
        .attr('font-weight', '600')
        // i is a loop counter; RING_ORDER is a const enum array — neither user-controlled
        // eslint-disable-next-line security/detect-object-injection
        .attr('fill', RING_COLORS[RING_ORDER[i]])
        // eslint-disable-next-line security/detect-object-injection
        .text(RING_ORDER[i]);
    }

    // Sector boundary lines + outer labels.
    sectors.forEach((sector, i) => {
      const boundary = angleSlice * i - Math.PI / 2;
      g.append('line')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', maxRadius * Math.cos(boundary))
        .attr('y2', maxRadius * Math.sin(boundary))
        .attr('stroke', 'var(--forge-theme-border, #e0e0e0)')
        .attr('stroke-width', 0.5);

      const mid = boundary + angleSlice / 2;
      const labelR = maxRadius + 22;
      const lx = labelR * Math.cos(mid);
      const ly = labelR * Math.sin(mid);
      g.append('text')
        .attr('x', lx)
        .attr('y', ly)
        .attr('dy', '0.35em')
        .attr('text-anchor', lx < -2 ? 'end' : lx > 2 ? 'start' : 'middle')
        .attr('font-size', '11px')
        .attr('font-weight', '600')
        .attr('fill', 'var(--forge-theme-text-primary, #212121)')
        .text(sector.label.length > 24 ? `${sector.label.slice(0, 23)}…` : sector.label);
    });

    // Scatter each blip randomly within its sector+ring cell. Positions are derived from a stable
    // hash of the blip id (not Math.random) so they look dispersed but don't jump on re-render.
    const placed: PlacedBlip[] = [];
    for (const blip of blips) {
      const si = sectorIndex.get(sectorOf(blip));
      const ri = ringIndex.get(blip.ring);
      if (si === undefined || ri === undefined) continue;

      const sectorStart = angleSlice * si - Math.PI / 2;
      const ringInner = ringInnerRadius(ri);
      const ringWidth = ringOuterRadius(ri) - ringInner;
      // Keep a margin off the wedge edges and ring boundaries so blips stay legibly inside the cell.
      const angle = sectorStart + (0.1 + 0.8 * hashUnit(blip.id, 1)) * angleSlice;
      const r = ringInner + (0.15 + 0.7 * hashUnit(blip.id, 2)) * ringWidth;
      placed.push({ blip, x: r * Math.cos(angle), y: r * Math.sin(angle) });
    }

    // Draw blips.
    placed.forEach(({ blip, x, y }) => {
      const matched = !filtersActive || matchedIds.has(blip.id);
      const isSelected = selectedId === blip.id;
      const color = RING_COLORS[blip.ring];

      const node = g
        .append('g')
        .attr('class', 'tech-radar-blip')
        .attr('tabindex', 0)
        .attr('role', 'button')
        .attr('aria-label', `${blip.name} — ${blip.ring}, ${blip.family}`)
        .style('cursor', 'pointer')
        .style('opacity', matched ? 1 : 0.12)
        .on('click', () => onSelect(blip))
        .on('keydown', (event: KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelect(blip);
          }
        });

      node
        .append('circle')
        .attr('cx', x)
        .attr('cy', y)
        .attr('r', isSelected ? 8 : 6)
        .attr('fill', color)
        .attr('stroke', isSelected ? 'var(--forge-theme-text-primary, #212121)' : 'white')
        .attr('stroke-width', isSelected ? 2.5 : 1.5);

      // "New" blips get an extra outer ring.
      if (blip.isNew) {
        node
          .append('circle')
          .attr('cx', x)
          .attr('cy', y)
          .attr('r', 10)
          .attr('fill', 'none')
          .attr('stroke', color)
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '2,2');
      }

      node.append('title').text(`${blip.name} (${blip.ring}${blip.isNew ? ', new' : ''})`);
    });
  }, [sectors, blips, matchedIds, filtersActive, selectedId, onSelect, sectorOf, size]);

  return (
    <div className="tech-radar-chart">
      <svg
        ref={svgRef}
        width={size}
        height={size}
        role="img"
        aria-label="Tech Radar: technologies placed by strategic lifecycle ring and product-family sector"
      />
    </div>
  );
}
